import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { FiPrinter, FiX, FiImage } from 'react-icons/fi';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';
import axios from 'axios';

function WorkOrderPrint({ job, timeEntries = [], onClose }) {
  const printRef = useRef();
  const [dailyEntries, setDailyEntries] = useState([]);
  const [dailyLoaded, setDailyLoaded] = useState(false);

  // Helper to parse UTC timestamps from backend
  const parseUTCDate = (dateStr) => {
    if (!dateStr) return null;
    let timestamp = dateStr;
    // Add Z suffix if not present to treat as UTC
    if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
      timestamp = timestamp + 'Z';
    }
    return new Date(timestamp);
  };

  // Fetch daily time entries for the team members who worked on this job
  useEffect(() => {
    const fetchDailyEntries = async () => {
      try {
        const token = await getAuthToken();
        const config = {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        };
        const response = await axios.get(`${API_URL}/time-entries?type=daily`, config);
        setDailyEntries(response.data.time_entries || response.data || []);
        setDailyLoaded(true);
      } catch (error) {
        console.error('Error loading daily entries:', error);
        setDailyLoaded(true); // Still mark as loaded so we don't wait forever
      }
    };
    fetchDailyEntries();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Work_Order_${job.job_number}`,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '____/____/____';
    // Handle plain date strings (YYYY-MM-DD) directly to avoid Safari parsing issues
    const dateOnly = dateStr.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    const date = parseUTCDate(dateStr);
    if (!date || isNaN(date.getTime())) return '____/____/____';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = parseUTCDate(dateStr);
    if (!date) return '';
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    return `${hours}:${minutes}`;
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '____/____/____';
    // Handle plain date strings (YYYY-MM-DD) directly to avoid Safari parsing issues
    const dateOnly = dateStr.split('T')[0];
    const parts = dateOnly.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0].slice(-2)}`;
    }
    const date = parseUTCDate(dateStr);
    if (!date || isNaN(date.getTime())) return '____/____/____';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  // Group time entries by date and match with daily entries
  const processTimeEntries = () => {
    const grouped = {};
    
    // Group job time entries by date (use local date for grouping)
    timeEntries.forEach(entry => {
      if (!entry.clock_in) return;
      
      const date = parseUTCDate(entry.clock_in);
      if (!date) return;
      // Use local date for grouping (YYYY-MM-DD in local timezone)
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: entry.clock_in,
          jobEntries: [],
          men: new Set(),
          memberIds: new Set(),
          totalHours: 0
        };
      }
      
      grouped[dateKey].jobEntries.push(entry);
      grouped[dateKey].men.add(entry.team_member_name || entry.member_name);
      if (entry.team_member_id) grouped[dateKey].memberIds.add(entry.team_member_id);
      grouped[dateKey].totalHours += entry.total_hours || 0;
    });
    
    // Convert to array and sort by date
    return Object.entries(grouped)
      .sort((a, b) => parseUTCDate(a[1].date) - parseUTCDate(b[1].date))
      .map(([dateKey, group]) => {
        // Find earliest job arrival and latest job departure
        let earliestArrive = null;
        let latestDepart = null;
        const descriptions = new Set();
        
        group.jobEntries.forEach(entry => {
          if (entry.clock_in) {
            const inTime = parseUTCDate(entry.clock_in);
            if (inTime && (!earliestArrive || inTime < earliestArrive)) earliestArrive = inTime;
          }
          if (entry.clock_out) {
            const outTime = parseUTCDate(entry.clock_out);
            if (outTime && (!latestDepart || outTime > latestDepart)) latestDepart = outTime;
          }
          if (entry.work_type) descriptions.add(entry.work_type);
          if (entry.notes) descriptions.add(entry.notes);
        });
        
        // Find daily clock in/out for team members on this date
        let earliestStart = null;
        let latestFinish = null;
        let foundDailyMatch = false;
        
        dailyEntries.forEach(daily => {
          if (!daily.clock_in) return;
          const dailyDate = parseUTCDate(daily.clock_in);
          if (!dailyDate) return;
          const dailyDateKey = `${dailyDate.getFullYear()}-${String(dailyDate.getMonth() + 1).padStart(2, '0')}-${String(dailyDate.getDate()).padStart(2, '0')}`;
          
          // Check if this daily entry is from same date and a team member who worked on the job
          if (dailyDateKey === dateKey) {
            const dailyMemberId = daily.team_member_id;
            const dailyMemberName = daily.team_member_name || daily.member_name;
            
            // Match by member ID or name
            const isMatch = group.memberIds.has(dailyMemberId) || group.men.has(dailyMemberName);
            
            if (isMatch) {
              foundDailyMatch = true;
              if (daily.clock_in) {
                const startTime = parseUTCDate(daily.clock_in);
                if (startTime && (!earliestStart || startTime < earliestStart)) earliestStart = startTime;
              }
              if (daily.clock_out) {
                const finishTime = parseUTCDate(daily.clock_out);
                if (finishTime && (!latestFinish || finishTime > latestFinish)) latestFinish = finishTime;
              }
            }
          }
        });
        
        // Only show Start/Finish if they're DIFFERENT from Arrive/Depart
        // If times match (within 1 minute), it means there's no separate daily entry
        const timesAreSame = (t1, t2) => {
          if (!t1 || !t2) return false;
          return Math.abs(t1.getTime() - t2.getTime()) < 60000; // Within 1 minute
        };
        
        const startIsDifferent = earliestStart && earliestArrive && !timesAreSame(earliestStart, earliestArrive);
        const finishIsDifferent = latestFinish && latestDepart && !timesAreSame(latestFinish, latestDepart);
        
        return {
          date: group.date,
          // Only include start/finish if they differ from job arrive/depart times
          start: (dailyLoaded && foundDailyMatch && startIsDifferent) ? earliestStart.toISOString() : null,
          arrive: earliestArrive ? earliestArrive.toISOString() : null,
          depart: latestDepart ? latestDepart.toISOString() : null,
          finish: (dailyLoaded && foundDailyMatch && finishIsDifferent) ? latestFinish.toISOString() : null,
          men: group.men.size,
          description: Array.from(descriptions).filter(d => d).join(', '),
          hours: group.totalHours.toFixed(1)
        };
      });
  };

  // Use useMemo to recalculate when dailyEntries or timeEntries change
  const processedEntries = useMemo(() => processTimeEntries(), [timeEntries, dailyEntries, dailyLoaded]);

  // Build county/land lot/district/section string
  const buildLegalDescription = () => {
    const parts = [];
    if (job.county) parts.push(job.county);
    if (job.land_lot) parts.push(`LL ${job.land_lot}`);
    if (job.district) parts.push(`Dist ${job.district}`);
    if (job.section) parts.push(`Sect ${job.section}`);
    return parts.join(' / ') || '';
  };

  // Light blue color for backgrounds (matching original PDF)
  const lightBlue = '#dbeafe';

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000,
      padding: '20px'
    },
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      maxHeight: 'calc(95dvh - 90px)',
      overflow: 'auto',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px 20px',
      borderBottom: '1px solid #ddd',
      backgroundColor: '#f8f9fa',
      position: 'sticky',
      top: 0,
      zIndex: 10
    },
    printBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    closeBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '18px'
    },
    // Print-specific styles
    page: {
      width: '8.5in',
      minHeight: '11in',
      padding: '0.4in',
      backgroundColor: 'white',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      lineHeight: '1.3',
      color: '#000'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '15px',
      position: 'relative',
      minHeight: '70px'
    },
    jobNumberBox: {
      border: '2px solid #000',
      padding: '8px 12px',
      fontSize: '12px',
      fontWeight: 'bold',
      maxWidth: '220px',
      wordWrap: 'break-word',
      backgroundColor: lightBlue
    },
    logoArea: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center'
    },
    logo: {
      maxWidth: '200px',
      height: 'auto'
    },
    datesBox: {
      textAlign: 'right',
      fontSize: '11px',
      minWidth: '160px'
    },
    dateLine: {
      marginBottom: '3px'
    },
    thickDivider: {
      borderTop: '3px solid #000',
      marginBottom: '5px'
    },
    contactBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '5px',
      fontSize: '14px'
    },
    twoColumn: {
      display: 'flex',
      gap: '30px',
      marginBottom: '10px'
    },
    column: {
      flex: 1
    },
    columnTitle: {
      fontWeight: 'bold',
      marginBottom: '8px',
      fontSize: '12px'
    },
    fieldRow: {
      display: 'flex',
      marginBottom: '2px',
      borderBottom: '1px solid #000',
      backgroundColor: lightBlue
    },
    fieldLabel: {
      width: '100px',
      flexShrink: 0,
      fontWeight: 'normal',
      fontSize: '10px',
      padding: '2px 4px'
    },
    fieldValue: {
      flex: 1,
      minHeight: '14px',
      paddingLeft: '5px',
      borderLeft: '1px solid #000',
      padding: '2px 4px'
    },
    sectionTitle: {
      fontWeight: 'bold',
      marginBottom: '5px',
      fontSize: '11px'
    },
    descriptionBox: {
      border: '1px solid #000',
      minHeight: '60px',
      padding: '5px',
      marginBottom: '10px',
      whiteSpace: 'pre-wrap',
      backgroundColor: lightBlue
    },
    timeTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '10px',
      fontSize: '10px'
    },
    tableHeader: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
      border: '1px solid #000',
      padding: '4px',
      textAlign: 'center'
    },
    tableCell: {
      border: '1px solid #000',
      padding: '4px',
      height: '20px',
      backgroundColor: lightBlue
    },
    notesAndImageRow: {
      display: 'flex',
      gap: '15px',
      marginBottom: '15px'
    },
    notesBox: {
      border: '1px solid #000',
      minHeight: '200px',
      padding: '5px',
      whiteSpace: 'pre-wrap',
      flex: 1,
      backgroundColor: lightBlue
    },
    parcelImageBox: {
      border: '2px solid #000',
      width: '320px',
      height: '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff',
      flexShrink: 0
    },
    signatureLine: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px',
      marginBottom: '15px'
    },
    signature: {
      borderTop: '1px solid #000',
      paddingTop: '3px',
      width: '250px',
      fontSize: '10px'
    },
    dateSig: {
      borderTop: '1px solid #000',
      paddingTop: '3px',
      width: '150px',
      fontSize: '10px',
      textAlign: 'center'
    },
    verse: {
      textAlign: 'center',
      fontSize: '9px',
      fontStyle: 'italic',
      marginTop: '10px',
      borderTop: '1px solid #ccc',
      paddingTop: '10px'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <h3 style={{ margin: 0 }}>Work Order Preview</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={styles.printBtn} onClick={handlePrint}>
              <FiPrinter size={18} />
              Print Work Order
            </button>
            <button style={styles.closeBtn} onClick={onClose}>
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Printable Content */}
        <div ref={printRef}>
          <div style={styles.page}>
            {/* Header Row */}
            <div style={styles.header}>
              <div style={styles.jobNumberBox}>
                <div style={{ fontSize: '11px', marginBottom: '4px' }}>JOB NUMBER</div>
                <div style={{ 
                  fontSize: job.job_number.length > 25 ? '12px' : job.job_number.length > 18 ? '14px' : '16px',
                  fontWeight: 'bold',
                  wordBreak: 'break-word'
                }}>
                  {job.job_number}
                </div>
              </div>
              
              <div style={styles.logoArea}>
                <img 
                  src="/NGS_Logo2.jpg" 
                  alt="North Georgia Surveying" 
                  style={styles.logo}
                />
              </div>
              
              <div style={styles.datesBox}>
                <div style={styles.dateLine}>Work Ordered: {formatDate(job.work_ordered_date)}</div>
                <div style={styles.dateLine}>Work Started: {formatDate(job.work_started_date)}</div>
                <div style={styles.dateLine}>Work Finished: {formatDate(job.work_finished_date)}</div>
              </div>
            </div>

            {/* Thick Divider */}
            <div style={styles.thickDivider}></div>

            {/* Contact Bar - Three Column */}
            <div style={styles.contactBar}>
              <span>Office: 706-625-1046</span>
              <span>292 Erwin Rd S.E. · Adairsville, GA 30103</span>
              <span>Cell: 706-625-1046</span>
            </div>

            {/* Thick Divider after contact */}
            <div style={styles.thickDivider}></div>

            {/* Two Column Section */}
            <div style={styles.twoColumn}>
              {/* Billing Information */}
              <div style={styles.column}>
                <div style={styles.columnTitle}>Billing Information</div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Person</span>
                  <span style={styles.fieldValue}>{job.billing_person || job.client_name || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Company</span>
                  <span style={styles.fieldValue}>{job.billing_company || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Address</span>
                  <span style={styles.fieldValue}>{job.billing_address || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>City/St./Zip</span>
                  <span style={styles.fieldValue}>{job.billing_city_state_zip || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Phone</span>
                  <span style={styles.fieldValue}>{job.billing_phone || job.client_phone || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Email</span>
                  <span style={styles.fieldValue}>{job.billing_email || job.client_email || ''}</span>
                </div>
              </div>

              {/* Job Location */}
              <div style={styles.column}>
                <div style={styles.columnTitle}>Job Location</div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Person</span>
                  <span style={styles.fieldValue}>{job.location_person || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Subdivision</span>
                  <span style={styles.fieldValue}>{job.subdivision || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Address</span>
                  <span style={styles.fieldValue}>{job.property_address || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>City/St./Zip</span>
                  <span style={styles.fieldValue}>{job.location_city_state_zip || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={styles.fieldLabel}>Parcel</span>
                  <span style={styles.fieldValue}>{job.parcel || ''}</span>
                </div>
                <div style={styles.fieldRow}>
                  <span style={{ ...styles.fieldLabel, width: '110px', fontSize: '9px' }}>Cnty/LL/Dist/Sect</span>
                  <span style={styles.fieldValue}>{buildLegalDescription()}</span>
                </div>
              </div>
            </div>

            {/* Thick Divider */}
            <div style={styles.thickDivider}></div>

            {/* Description of Work */}
            <div style={styles.sectionTitle}>Description of Work Required</div>
            <div style={styles.descriptionBox}>
              {job.description_of_work || ''}
            </div>

            {/* Thick Divider */}
            <div style={styles.thickDivider}></div>

            {/* Time Tracking Table */}
            <table style={styles.timeTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeader, width: '90px' }}>Date</th>
                  <th style={{ ...styles.tableHeader, width: '50px' }}>Start</th>
                  <th style={{ ...styles.tableHeader, width: '50px' }}>Arrive</th>
                  <th style={{ ...styles.tableHeader, width: '50px' }}>Depart</th>
                  <th style={{ ...styles.tableHeader, width: '50px' }}>Finish</th>
                  <th style={{ ...styles.tableHeader, width: '40px' }}>Men</th>
                  <th style={{ ...styles.tableHeader, width: '50px' }}>Hours</th>
                  <th style={styles.tableHeader}>Description</th>
                </tr>
              </thead>
              <tbody>
                {/* Render actual time entries first */}
                {processedEntries.map((entry, i) => (
                  <tr key={i}>
                    <td style={{ ...styles.tableCell, fontSize: '12px' }}>{formatDateOnly(entry.date)}</td>
                    <td style={styles.tableCell}>{formatTime(entry.start) || ''}</td>
                    <td style={styles.tableCell}>{formatTime(entry.arrive) || ''}</td>
                    <td style={styles.tableCell}>{formatTime(entry.depart) || ''}</td>
                    <td style={styles.tableCell}>{formatTime(entry.finish) || ''}</td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>{entry.men}</td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>{entry.hours}</td>
                    <td style={styles.tableCell}>{entry.description}</td>
                  </tr>
                ))}
                {/* Fill remaining rows up to 8 total */}
                {[...Array(Math.max(0, 8 - processedEntries.length))].map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td style={{ ...styles.tableCell, fontSize: '12px' }}>____/____/____</td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                    <td style={styles.tableCell}></td>
                  </tr>
                ))}
                {/* Total row if there are entries */}
                {processedEntries.length > 0 && (
                  <tr>
                    <td colSpan="5" style={{ ...styles.tableCell, textAlign: 'right', fontWeight: 'bold', backgroundColor: '#fff' }}>TOTAL:</td>
                    <td style={{ ...styles.tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#fff' }}>
                      {new Set(timeEntries.map(e => e.team_member_name || e.member_name)).size}
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#fff' }}>
                      {timeEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0).toFixed(1)}
                    </td>
                    <td style={{ ...styles.tableCell, backgroundColor: '#fff' }}></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Thick Divider */}
            <div style={styles.thickDivider}></div>

            {/* Job Specific Notes and Parcel Image Side by Side */}
            <div style={styles.notesAndImageRow}>
              <div style={{ flex: 1 }}>
                <div style={styles.sectionTitle}>Job Specific Notes</div>
                <div style={styles.notesBox}>
                  {job.job_specific_notes || ''}
                </div>
              </div>
              
              {/* Parcel Image */}
              <div style={styles.parcelImageBox}>
                {job.parcel_image_url ? (
                  <img 
                    src={`${API_BASE_URL || ''}${job.parcel_image_url}`}
                    alt="Parcel"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    <FiImage size={40} />
                    <div style={{ fontSize: '9px', marginTop: '5px' }}>Parcel Image</div>
                  </div>
                )}
              </div>
            </div>

            {/* Signature Line */}
            <div style={styles.signatureLine}>
              <div style={styles.signature}>
                Surveyor Signature of Job Completion
              </div>
              <div style={styles.dateSig}>
                Date____/____/____
              </div>
            </div>

            {/* Bible Verse */}
            <div style={styles.verse}>
              <strong>Deuteronomy 19:14</strong><br />
              Thou Shalt not remove thy neighbour's landmark, which they of old time have set in thine<br />
              inheritance, which thou shalt inherit in the land that the LORD thy God giveth thee to possess it.
            </div>
          </div>
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            @page {
              size: letter;
              margin: 0.25in;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

export default WorkOrderPrint;